var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { forwardRef, useCallback, useEffect, useState, } from "react";
import { createPortal } from "react-dom";
import { Editor, Range } from "slate";
import { ReactEditor, useSlateStatic } from "slate-react";
import useComponents from "../useComponents";
import { editorToDomNode } from "../utils";
const getIndexFromChildren = (children) => {
    const containsComboboxItems = React.Children.toArray(children).some((child) => React.isValidElement(child) &&
        child.type.displayName === "ComboboxItem");
    const i = React.Children.toArray(children).findIndex((child) => React.isValidElement(child) &&
        child.type.displayName === "ComboboxItem" &&
        child.props.selected);
    return i === -1 ? (containsComboboxItems ? 0 : undefined) : i;
};
const Portal = ({ children }) => {
    return typeof document === "object"
        ? createPortal(children, document.body)
        : null;
};
const ComboboxItem = forwardRef((props, ref) => {
    const { comboboxItemComponent: Component } = useComponents();
    return _jsx(Component, Object.assign({}, props, { ref: ref }));
});
ComboboxItem.displayName = "ComboboxItem";
function setComboboxStyle(comboboxElem, rect) {
    try {
        if (comboboxElem.offsetWidth + rect.left + window.pageXOffset <
            window.innerWidth - 16) {
            comboboxElem.style.left = `${rect.left + window.pageXOffset}px`;
            comboboxElem.style.right = "auto";
        }
        else {
            comboboxElem.style.left = "auto";
            comboboxElem.style.right = "16px";
        }
        comboboxElem.style.top = `${rect.top + window.pageYOffset + 24}px`;
    }
    catch (e) {
        //
    }
}
function setComboboxPositionByRange(editor, comboboxElem, range) {
    if (!comboboxElem) {
        return;
    }
    try {
        const domRange = ReactEditor.toDOMRange(editor, range);
        const rect = domRange.getBoundingClientRect();
        setComboboxStyle(comboboxElem, rect);
    }
    catch (e) {
        //
    }
}
function setComboboxPositionByCursor(editor, comboboxElem) {
    const { selection } = editor;
    if (selection && Range.isCollapsed(selection)) {
        const [start] = Range.edges(selection);
        const before = Editor.before(editor, start, { unit: "character" });
        const range = before && Editor.range(editor, before, start);
        const domRange = range && ReactEditor.toDOMRange(editor, range);
        const rect = domRange && domRange.getBoundingClientRect();
        if (!rect || !comboboxElem) {
            return;
        }
        setComboboxStyle(comboboxElem, rect);
    }
}
const ComboboxRoot = forwardRef((props, ref) => {
    const { open = false, onClose, onSelectItem, range, children } = props, other = __rest(props, ["open", "onClose", "onSelectItem", "range", "children"]);
    const [index, setIndex] = useState(() => getIndexFromChildren(children));
    const { comboboxComponent: Component, comboboxRootStyle } = useComponents();
    const editor = useSlateStatic();
    const numItems = React.Children.toArray(children).reduce((prev, curr) => React.isValidElement(curr) &&
        curr.type.displayName === "ComboboxItem"
        ? prev + 1
        : prev, 0);
    const [comboboxElem, setComboboxElem] = useState(null);
    const handleArrowDownPress = useCallback((event) => {
        if (numItems === 0 || typeof index !== "number") {
            return;
        }
        event.preventDefault();
        const newIndex = index < numItems - 1 ? index + 1 : 0;
        onSelectItem === null || onSelectItem === void 0 ? void 0 : onSelectItem(newIndex);
        setIndex(newIndex);
    }, [index, numItems, onSelectItem]);
    const handleArrowUpPress = useCallback((event) => {
        if (numItems === 0 || typeof index !== "number") {
            return;
        }
        event.preventDefault();
        const newIndex = index === 0 ? numItems - 1 : index - 1;
        onSelectItem === null || onSelectItem === void 0 ? void 0 : onSelectItem(newIndex);
        setIndex(newIndex);
    }, [index, numItems, onSelectItem]);
    const handleTabPress = useCallback((event) => {
        onClose(event, "tabPress", index);
    }, [index, onClose]);
    const handleEnterPress = useCallback((event) => {
        onClose(event, "enterPress", index);
    }, [index, onClose]);
    const handleEscapePress = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        onClose(event, "escapePress", index);
    }, [index, onClose]);
    const handleSpacePress = useCallback((event) => {
        onClose(event, "spacePress", index);
    }, [index, onClose]);
    const handleKeyDown = useCallback((event) => {
        switch (event.key) {
            case "ArrowDown":
                handleArrowDownPress(event);
                break;
            case "ArrowUp":
                handleArrowUpPress(event);
                break;
            case "Tab":
                handleTabPress(event);
                break;
            case "Enter":
                handleEnterPress(event);
                break;
            case "Escape":
                handleEscapePress(event);
                break;
            case " ":
                handleSpacePress(event);
                break;
        }
    }, [
        handleArrowDownPress,
        handleArrowUpPress,
        handleEnterPress,
        handleEscapePress,
        handleSpacePress,
        handleTabPress,
    ]);
    const handleBlur = useCallback((event) => {
        onClose(event, "clickAway", index);
    }, [index, onClose]);
    useEffect(() => {
        const editorElem = editorToDomNode(editor);
        if (open) {
            editorElem === null || editorElem === void 0 ? void 0 : editorElem.addEventListener("keydown", handleKeyDown);
            editorElem === null || editorElem === void 0 ? void 0 : editorElem.addEventListener("blur", handleBlur);
        }
        return () => {
            editorElem === null || editorElem === void 0 ? void 0 : editorElem.removeEventListener("keydown", handleKeyDown);
            editorElem === null || editorElem === void 0 ? void 0 : editorElem.removeEventListener("blur", handleBlur);
        };
    }, [index, editor, open, onClose, handleKeyDown, handleBlur]);
    useEffect(() => {
        if (open) {
            setIndex(getIndexFromChildren(children));
            if (range) {
                setComboboxPositionByRange(editor, comboboxElem, range);
            }
            else {
                setComboboxPositionByCursor(editor, comboboxElem);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, comboboxElem, range, editor]);
    return (_jsx(Portal, { children: _jsx("div", Object.assign({ 
            // keep focus in text field
            onMouseDown: (e) => e.preventDefault(), onTouchStart: (e) => e.preventDefault(), style: Object.assign({ top: "-9999px", left: "-9999px", position: "absolute", display: open ? "block" : "none" }, comboboxRootStyle), ref: setComboboxElem }, { children: _jsx(Component, Object.assign({ in: open, ref: ref }, other, { children: React.Children.map(children, (child, i) => {
                    if (!React.isValidElement(child)) {
                        return null;
                    }
                    return React.cloneElement(child, Object.assign(Object.assign({}, child.props), { selected: i === index }), child.props.children);
                }) })) })) }));
});
export default ComboboxRoot;
export { ComboboxItem };
